import {Component, EventEmitter, Input, Output} from '@angular/core';
import {combineLatest, forkJoin, of, Subject} from 'rxjs';
import {filter, map, switchMap, tap} from 'rxjs/operators';
import {ComponentEvent, Page, Pageable, StateAwareComponent} from 'src/app/model/rest/base';
import * as LessonsV3 from 'src/app/model/v3/lessons';
import {LocalStateService} from "../../services/local-state.service";
import {PeopleV3RestService} from "../../services/rest/v3/people-rest.service";
import {ManagerColV3RestService} from "../../services/rest/v3/manager-col-v3-rest.service";

export enum LessonType {
  Upcomming, Past
}

export class StudentLessonsComponentEvent extends ComponentEvent {
}

@Component({
  selector: 'app-student-lessons',
  templateUrl: './student-lessons.component.html',
  styleUrls: ['./student-lessons.component.css'],
  standalone: false
})
export class StudentLessonsComponent extends StateAwareComponent<StudentLessonsComponentEvent> {

  _lessonType: LessonType;
  _pageable: Pageable;
  _studentId: number;
  _schoolId: number = null;

  private schoolIdSubject = new Subject<number>();
  private studentIdSubject = new Subject<number>();
  private lessonTypeSubject = new Subject<LessonType>();
  private contextPath: string;
  private teacherPhotoUrls = new Map<number, string | null>();

  _data: Page<LessonsV3.LFStudentGeneral>;

  @Output()
  dataLoaded = new EventEmitter<Page<LessonsV3.LFStudentGeneral>>();

  @Output()
  currentPageable = new EventEmitter<Pageable>();

  set pageable(value: Pageable) {
    this._pageable = value;
    this.currentPageable.emit(value);
  }

  get pageable() {
    return this._pageable;
  }

  set data(value: Page<LessonsV3.LFStudentGeneral>) {
    this._data = value;
    this.dataLoaded.emit(value);
  }

  get data() {
    return this._data;
  }

  @Input()
  set studentId(value: number) {
    this._studentId = value;
    this.studentIdSubject.next(value);
  }

  get studentId() {
    return this._studentId;
  }

  constructor(
    private managerV3Rest: ManagerColV3RestService,
    private peopleV3Rest: PeopleV3RestService,
    private localState: LocalStateService
  ) {
    super();

    combineLatest(
      this.schoolIdSubject,
      this.studentIdSubject,
      this.lessonTypeSubject
    ).pipe(
      filter<[number, number, LessonType]>(([schoolId, studentId, lessonType]) => schoolId != null && studentId != null && lessonType != null),
      tap(() => this.clearData()),
      tap(() => this.prepareContextPath()),
      switchMap(() => this.loadCurrentState()),
      switchMap((pageable) => this.loadPage(pageable))
    ).subscribe();
  }

  private loadCurrentState() {
    return this.localState.get<Pageable>(this.contextPath, () => {
      if (this._lessonType === LessonType.Upcomming) {
        return Pageable.of(0, 10, ['metric.details.plannedStartDate,ASC']);
      }
      return Pageable.of(0, 10, ['metric.details.plannedStartDate,DESC']);
    }).pipe(
      tap((pageable) => this.pageable = pageable)
    );
  }

  private prepareContextPath() {
    this.contextPath = `/school/${this._schoolId}/students/${this._studentId}/lessons/${this._lessonType}`;
  }

  @Output()
  lessonDetailsEvent = new EventEmitter<LessonsV3.LFStudentGeneral>();

  @Input()
  public set lessonType(lessonType: LessonType) {
    this._lessonType = lessonType;
    this.lessonTypeSubject.next(lessonType);
  }

  @Input()
  public set schoolId(schoolId: number) {
    this._schoolId = schoolId;
    this.schoolIdSubject.next(schoolId);
  }

  public hasResults(): boolean {
    return this.data != null;
  }

  clearData(): void {
    this.pageable = null;
    this.data = null;
  }

  private getPersonName(person: { name?: string; surname?: string } | null): string {
    if (!person) {
      return '';
    }
    return [person.name, person.surname].filter((part) => !!part).join(' ');
  }

  private getInitialsFromPerson(person: { name?: string; surname?: string } | null): string {
    if (!person) {
      return '';
    }
    return [person.name, person.surname].filter((part) => !!part).map((part) => part[0]).join('');
  }

  public getLessonTeacherPhotoUrl(lesson: LessonsV3.LFStudentGeneral): string | null {
    const personId = lesson?.teacher?.reference?.person?.id;
    if (!personId) {
      return null;
    }
    return this.teacherPhotoUrls.get(personId) ?? null;
  }

  public getLessonTeacher(lesson: LessonsV3.LFStudentGeneral): string {
    return this.getPersonName(lesson?.teacher?.reference?.person ?? null);
  }

  public getLessonDate(lesson: LessonsV3.LFStudentGeneral): Date {
    return lesson.lessonMetric.plannedStartDate;
  }

  public mayBeViewed(_lesson: LessonsV3.LFStudentGeneral): boolean {
    return true;
  }

  private loadPage(pageable: Pageable) {
    this.pageable = pageable;
    return this.localState.set<Pageable>(this.contextPath, pageable).pipe(
      switchMap((page) => this.callForLessons(page)),
      switchMap((resultPage) => this.loadTeacherPhotos(resultPage).pipe(
        map(() => resultPage)
      )),
      tap((resultPage) => this.data = resultPage)
    );
  }

  switchPage(pageable: Pageable) {
    this.loadPage(pageable).subscribe();
  }

  private callForLessons(pageable: Pageable) {
    const now = new Date();
    const boundary = new Date(now.getTime() - 5 * 60 * 1000);
    const isUpcoming = this._lessonType === LessonType.Upcomming;
    // The boundary alone decides which card a lesson goes to, so both ask for the same
    // statuses. Initializing is the only one left out: those are unfinished reservation
    // drafts that the student side never showed.
    const statuses = [
      LessonsV3.LessonStatus.Booked,
      LessonsV3.LessonStatus.Due,
      LessonsV3.LessonStatus.InProgress,
      LessonsV3.LessonStatus.Complete,
      LessonsV3.LessonStatus.Cancel,
      LessonsV3.LessonStatus.InvalidatedByStudent,
      LessonsV3.LessonStatus.InvalidatedByTeacher,
    ];

    return this.managerV3Rest.findLessons(
      'col',
      null,
      'student-full',
      null,
      this.studentId,
      null,
      null,
      null,
      null,
      statuses,
      isUpcoming ? boundary : null,
      isUpcoming ? null : boundary,
      pageable
    ).pipe(
      map((page) => page as Page<LessonsV3.LFStudentGeneral>)
    );
  }

  private loadTeacherPhotos(page: Page<LessonsV3.LFStudentGeneral>) {
    const personIds = Array.from(new Set(
      (page.content ?? [])
        .map((lesson) => lesson?.teacher?.reference?.person?.id)
        .filter((personId): personId is number => !!personId)
    ));
    if (personIds.length === 0) {
      return of(null);
    }

    return forkJoin(
      personIds.map((personId) =>
        this.peopleV3Rest.getPhoto(personId).pipe(
          tap((photo) => {
            this.teacherPhotoUrls.set(personId, photo?.publicAccessUrl ? `url(${photo.publicAccessUrl})` : null);
          })
        )
      )
    );
  }

  public lessonBadgeClass(lesson: LessonsV3.LFStudentGeneral) {
    if (this._lessonType === undefined) {
      return '';
    }
    return this.getStudentLessonBorderColorClass(lesson);
  }

  public getInitials(lesson: LessonsV3.LFStudentGeneral) {
    return this.getInitialsFromPerson(lesson?.teacher?.reference?.person ?? null);
  }

  private getStudentLessonBorderColorClass(lesson: LessonsV3.LFStudentGeneral): string {
    if (!lesson) {
      return '';
    }
    switch (lesson.status) {
      case LessonsV3.LessonStatus.Booked:
        return 'lesson-status booked';
      case LessonsV3.LessonStatus.Complete:
        return 'lesson-status complete';
      case LessonsV3.LessonStatus.Due:
        return 'lesson-status due';
      case LessonsV3.LessonStatus.InProgress:
        return 'lesson-status in-progress';
      case LessonsV3.LessonStatus.InvalidatedByTeacher:
      case LessonsV3.LessonStatus.InvalidatedByStudent:
      case LessonsV3.LessonStatus.Cancel:
        return 'lesson-status student-invalidate';
      default:
        return '';
    }
  }
}
